/**
 * whatsappLink — fonte única para abrir o WhatsApp de um negócio.
 *
 * - Sanitiza o número (apenas dígitos) e garante DDI 55 (Brasil).
 * - Monta a mensagem publicitária padrão "Empreende {cidade}".
 * - Variante automática quando o negócio tem promoção ativa.
 * - Dispara analytics (sendBeacon) para `POST /api/analytics/whatsapp-click/{id}`.
 *
 * Uso típico em handlers de clique:
 *   import { openWhatsApp } from '../utils/whatsappLink';
 *   onClick={(e) => { e.stopPropagation(); openWhatsApp({ business, municipio }); }}
 */

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const capitalize = (s) => (s && typeof s === 'string')
  ? s.charAt(0).toUpperCase() + s.slice(1)
  : '';

/**
 * Resolve o nome amigável do município a partir das pistas disponíveis.
 *   - municipio.nome (preferido)
 *   - municipio_slug → "ponte-nova" → "Ponte Nova"
 *   - fallback "sua cidade"
 */
function resolveMunicipioNome(municipio, business) {
  if (municipio?.nome) return municipio.nome;
  const slug = municipio?.slug
    || business?.municipio_slug
    || business?.municipio_nome
    || '';
  if (!slug) return 'sua cidade';
  return slug
    .replace(/-/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(capitalize)
    .join(' ');
}

function resolveCategoria(business) {
  return business?.categoria_nome
    || business?.subcategoria
    || business?.categoria
    || 'seus serviços';
}

/**
 * Mensagem padrão (sem promoção).
 */
export function buildWhatsAppMessage({ business, municipio }) {
  const cidade = resolveMunicipioNome(municipio, business);
  const categoria = resolveCategoria(business);
  return (
    `Olá, *${business?.nome || 'negócio'}*! 🌟\n\n` +
    `Encontrei vocês no *Empreende ${cidade}* — ` +
    `o marketplace oficial da nossa cidade — ` +
    `e me interessei pelo que vocês oferecem em ${categoria}.\n\n` +
    `Quero saber mais sobre como vocês podem me ajudar! 💬`
  );
}

/**
 * Mensagem quando o usuário está clicando porque há promoção/cupom ativo.
 */
export function buildWhatsAppMessageComPromocao({ business, municipio, promocao }) {
  const cidade = resolveMunicipioNome(municipio, business);
  const titulo = promocao?.titulo || promocao?.nome || 'a promoção';
  return (
    `Olá, *${business?.nome || 'negócio'}*! 🌟\n\n` +
    `Encontrei vocês no *Empreende ${cidade}* — ` +
    `o marketplace oficial da nossa cidade — ` +
    `e me interessei pela promoção *${titulo}*.\n\n` +
    `Ainda está disponível? Quero aproveitar! 🎯`
  );
}

/**
 * Normaliza o número de WhatsApp: só dígitos + DDI 55 (Brasil).
 * Retorna `null` se não houver número válido.
 */
export function normalizeWhatsAppNumber(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return null;
  return digits.startsWith('55') ? digits : `55${digits}`;
}

/**
 * Dispara o evento de clique no backend, sem bloquear a UI.
 * Usa `sendBeacon` quando disponível; fallback para `fetch keepalive`.
 */
export function trackWhatsAppClick(businessId) {
  if (!businessId || !API_BASE) return;
  const url = `${API_BASE}/api/analytics/whatsapp-click/${encodeURIComponent(businessId)}`;
  try {
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([], { type: 'application/json' }));
      return;
    }
  } catch { /* fallback abaixo */ }
  try {
    fetch(url, { method: 'POST', keepalive: true }).catch(() => {});
  } catch { /* silent */ }
}

/**
 * Detecta se estamos em um touch device (mobile/tablet).
 * Em mobile usamos o deeplink `whatsapp://` que abre o app instalado.
 * No desktop usamos `web.whatsapp.com` direto (evita o redirect do `wa.me`
 * para `api.whatsapp.com` que algumas extensões/CSP bloqueiam → `ERR_BLOCKED_BY_RESPONSE`).
 */
function isMobileDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /android|iphone|ipad|ipod|mobile|opera mini/i.test(ua);
}

/**
 * Monta a URL final do WhatsApp considerando ambiente.
 *
 * @param {string} phone    — número já normalizado (com DDI 55, só dígitos)
 *                            quando vazio/null gera link "compose universal" (web.whatsapp.com/send?text=...)
 * @param {string} message  — mensagem já formatada
 */
function buildWhatsAppOpenUrl(phone, message) {
  const encoded = encodeURIComponent(message || '');
  if (isMobileDevice()) {
    // Deeplink universal — abre o app instalado em iOS/Android.
    return phone
      ? `whatsapp://send?phone=${phone}&text=${encoded}`
      : `whatsapp://send?text=${encoded}`;
  }
  // Desktop: WhatsApp Web evita o redirect 301 do `wa.me` → `api.whatsapp.com`.
  return phone
    ? `https://web.whatsapp.com/send?phone=${phone}&text=${encoded}`
    : `https://web.whatsapp.com/send?text=${encoded}`;
}

/**
 * Função principal: abre o WhatsApp em nova aba já com a mensagem certa
 * e registra o clique. Retorna `false` se o negócio não tem WhatsApp.
 *
 * @param {Object}  params
 * @param {Object}  params.business
 * @param {Object} [params.municipio]
 * @param {Object} [params.promocao]   — se informado, usa a variante de promoção
 */
export function openWhatsApp({ business, municipio, promocao } = {}) {
  const phone = normalizeWhatsAppNumber(business?.whatsapp);
  if (!phone) return false;

  const message = promocao
    ? buildWhatsAppMessageComPromocao({ business, municipio, promocao })
    : buildWhatsAppMessage({ business, municipio });

  const url = buildWhatsAppOpenUrl(phone, message);
  if (business?.id) trackWhatsAppClick(business.id);
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}

/**
 * Útil quando precisamos do href para um <a/> (sem onClick wrapper).
 * Não registra clique automaticamente — o caller é responsável.
 */
export function buildWhatsAppHref({ business, municipio, promocao } = {}) {
  const phone = normalizeWhatsAppNumber(business?.whatsapp);
  if (!phone) return null;
  const message = promocao
    ? buildWhatsAppMessageComPromocao({ business, municipio, promocao })
    : buildWhatsAppMessage({ business, municipio });
  return buildWhatsAppOpenUrl(phone, message);
}

/**
 * URL "compose universal" (sem telefone) — para compartilhar conteúdo no
 * WhatsApp pedindo ao usuário pra escolher o contato no próprio app.
 *
 * @param {string} text — mensagem completa já formatada
 */
export function buildWhatsAppShareUrl(text) {
  return buildWhatsAppOpenUrl('', text);
}
